import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { NotificationLog } from "@/hooks/useNotificationLogs";
import { CheckCircle2, XCircle, Mail } from "lucide-react";

interface NotificationLogsTableProps {
  logs: NotificationLog[];
}

const statusLabels: Record<string, string> = {
  'solicitada': 'Coleta solicitada',
  'pendente_analise': 'Pendente análise',
  'aguardando_aprovacao': 'Aguardando aprovação',
  'precificacao_aprovada': 'Precificação aprovada',
  'precificacao_recusada': 'Precificação recusada',
  'aguardando_agendamento': 'Aguardando agendamento',
  'coleta_agendada': 'Coleta agendada',
  'coleta_agendada_com_alteracao': 'Coleta reagendada',
  'coleta_agendada_alterada': 'Coleta reagendada',
  'coleta_confirmada': 'Coleta confirmada',
  'concluida': 'Concluída',
  'parcialmente_coletada': 'Parcialmente coletada',
  'coleta_nao_realizada': 'Não realizada',
  'cancelada': 'Cancelada',
};

const getStatusLabel = (status: string | null): string => {
  if (!status) return 'N/A';
  return statusLabels[status] || status;
};

export const NotificationLogsTable = ({ logs }: NotificationLogsTableProps) => {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[180px]">Data/Hora</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Remetente</TableHead>
            <TableHead>Destinatário</TableHead>
            <TableHead>Email Real</TableHead>
            <TableHead>Gerador</TableHead>
            <TableHead>Estabelecimento</TableHead>
            <TableHead>Mudança de Status</TableHead>
            <TableHead className="w-[100px]">Coleta</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                Nenhum log de notificação encontrado
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="font-mono text-xs">
                  {formatDate(new Date(log.sent_at), "dd/MM/yyyy HH:mm", {
                    locale: ptBR,
                  })}
                </TableCell>
                <TableCell>
                  {log.status === "sent" ? (
                    <Badge variant="default" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Enviado
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="gap-1">
                      <XCircle className="h-3 w-3" />
                      Falhou
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">{log.sender_email || "N/A"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{log.recipient}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{log.requester_email || "N/A"}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {log.generator_name || "N/A"}
                </TableCell>
                <TableCell className="text-sm">
                  {log.establishment_name || "N/A"}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className="text-xs">
                      {getStatusLabel(log.status_anterior)}
                    </Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant="secondary" className="text-xs">
                      {getStatusLabel(log.status_novo)}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {log.collect_id.slice(0, 8)}...
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
